import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { newIdempotencyKey, SubmitError, submitPublic } from '../lib/publicSubmit';
import { getAttribution } from '../lib/attribution';
import { track } from '../lib/analytics';
import Turnstile from '../components/Turnstile';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';
import { whatsappLink } from '../config/school';
import { useSchool } from '../contexts/SchoolContext';
import './Contact.css';

export const ENQUIRY_TYPES = [
  { value: 'general', label: 'General question' },
  { value: 'admissions', label: 'Admissions enquiry' },
  { value: 'visit_request', label: 'Request a school visit' }
];
const VISIT_SUBJECT = 'Request a school visit';
const PHONE = /^[0-9+()\-\s]{7,20}$/;

// /contact?type=visit_request (or ?type=admissions) opens the form with that enquiry type selected.
const initialForm = (search) => {
  const requested = new URLSearchParams(search).get('type');
  const type = ENQUIRY_TYPES.some((t) => t.value === requested) ? requested : 'general';
  return {
    type, name: '', email: '', phone: '', childName: '', grade: '',
    subject: type === 'visit_request' ? VISIT_SUBJECT : '', message: ''
  };
};

const Contact = () => {
  const { settings, grades } = useSchool();
  const location = useLocation();
  const whatsappPhone = settings.phones.find((phone) => phone.whatsapp);
  const [formData, setFormData] = useState(() => initialForm(location.search));
  const [consent, setConsent] = useState(false);

  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  // One key per logical submission: a double click or retry returns the original record.
  const idempotencyKey = useRef(newIdempotencyKey());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email.trim())) newErrors.email = 'Email is invalid';
    if (formData.phone.trim() && !PHONE.test(formData.phone.trim())) {
      newErrors.phone = 'Enter a valid phone number (digits, +, spaces, dashes, brackets)';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (!consent) newErrors.consent = 'Please accept the privacy policy';
    return newErrors;
  };

  const scrollToError = (errorFields) => {
    // Get the first error field name
    const firstErrorField = Object.keys(errorFields)[0];
    if (firstErrorField) {
      // Find the input element by name
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        // Scroll to the element with some offset
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 100; // 100px offset from top
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // Focus the element after scrolling
        setTimeout(() => {
          element.focus();
        }, 500);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitStatus({ type: 'error', message: 'Please fill in all required fields' });
      scrollToError(newErrors);
      return;
    }

    if (!turnstileToken) {
      setSubmitStatus({ type: 'error', message: 'Please complete the spam check and try again.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const { childName, ...rest } = formData;
      await submitPublic({
        kind: 'contact',
        payload: {
          ...Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, v.trim()])),
          child_name: childName.trim(),
          consent: true,
          ...getAttribution()
        },
        idempotencyKey: idempotencyKey.current,
        turnstileToken
      });

      track('enquiry_submitted', { enquiry_type: formData.type });
      setSubmitStatus({
        type: 'success',
        message: formData.type === 'visit_request'
          ? 'Thank you! We have received your visit request and will contact you to arrange a time.'
          : 'Thank you for contacting us! We will get back to you soon.'
      });
      setFormData(initialForm(''));
      setConsent(false);
      setErrors({});
      idempotencyKey.current = newIdempotencyKey();
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error instanceof SubmitError
          ? error.message
          : 'Failed to send message. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
      setTurnstileToken('');
      setTurnstileReset((n) => n + 1);
    }
  };

  // A labelled input with its error message tied to it for assistive technology.
  const textField = (name, label, { required = false, type = 'text', ...rest } = {}) => (
    <div className="form-group">
      <label htmlFor={`contact-${name}`}>{label}{required && <span aria-hidden="true"> *</span>}</label>
      <input
        id={`contact-${name}`}
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        className={errors[name] ? 'error' : ''}
        aria-required={required || undefined}
        aria-invalid={errors[name] ? 'true' : undefined}
        aria-describedby={errors[name] ? `contact-${name}-error` : undefined}
        {...rest}
      />
      {errors[name] && <span id={`contact-${name}-error`} className="error-message">{errors[name]}</span>}
    </div>
  );

  return (
    <div className="contact">
      <section className="contact-hero page-hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We’d love to hear from you — Get in touch
          </motion.p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info-section">
              <h2>Get in Touch</h2>
              <p>
                Have questions about our programs, admissions, or anything else? 
                Feel free to reach out to us. We're here to help!
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div>
                    <h3>Address</h3>
                    <p>{settings.school_name}<br />{settings.address}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <FaPhone />
                  </div>
                  <div>
                    <h3>Phone</h3>
                    {settings.phones.map((phone, index) => (
                      <p key={phone.tel}>
                        {index === 0 && 'Main: '}
                        <a href={`tel:${phone.tel}`}>{phone.display}</a>
                      </p>
                    ))}
                    {whatsappPhone && (
                      <p>WhatsApp: <a href={whatsappLink(whatsappPhone.tel, '')} target="_blank" rel="noopener noreferrer">Chat on WhatsApp</a></p>
                    )}
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <FaEnvelope />
                  </div>
                  <div>
                    <h3>Email</h3>
                    {settings.email_general && (
                      <p>General: <a href={`mailto:${settings.email_general}`}>{settings.email_general}</a></p>
                    )}
                    {settings.email_admissions && (
                      <p>Admissions: <a href={`mailto:${settings.email_admissions}`}>{settings.email_admissions}</a></p>
                    )}
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <FaClock />
                  </div>
                  <div>
                    <h3>Office Hours</h3>
                    <p>Monday - Saturday: 8:00 AM - 3:00 PM</p>
                    <p>Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-section">
              <h2>Send us a Message</h2>
              
              {submitStatus.message && (
                <div className={`alert alert-${submitStatus.type}`} role={submitStatus.type === 'error' ? 'alert' : 'status'}>
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                <p className="required-note"><span aria-hidden="true">*</span> Required</p>

                <div className="form-group">
                  <label htmlFor="contact-type">What is this about?</label>
                  <select id="contact-type" name="type" value={formData.type} onChange={handleChange}>
                    {ENQUIRY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                {textField('name', 'Your name', { required: true, autoComplete: 'name' })}

                <div className="form-row">
                  {textField('email', 'Email', { required: true, type: 'email', autoComplete: 'email' })}
                  {textField('phone', 'Phone', { type: 'tel', autoComplete: 'tel', inputMode: 'tel' })}
                </div>

                <div className="form-row">
                  {textField('childName', "Child's name", { autoComplete: 'off', maxLength: 200 })}
                  <div className="form-group">
                    <label htmlFor="contact-grade">Grade/Program of interest</label>
                    <select id="contact-grade" name="grade" value={formData.grade} onChange={handleChange}>
                      <option value="">Not sure / not applicable</option>
                      {(grades ?? []).map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                    </select>
                  </div>
                </div>

                {textField('subject', 'Subject', { required: true })}

                <div className="form-group">
                  <label htmlFor="contact-message">Message<span aria-hidden="true"> *</span></label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className={errors.message ? 'error' : ''}
                    aria-required="true"
                    aria-invalid={errors.message ? 'true' : undefined}
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    placeholder={formData.type === 'visit_request' ? 'Which days or times suit you?' : undefined}
                  ></textarea>
                  {errors.message && <span id="contact-message-error" className="error-message">{errors.message}</span>}
                </div>

                <div className="form-group consent-group">
                  <label htmlFor="contact-consent">
                    <input
                      id="contact-consent"
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => {
                        setConsent(e.target.checked);
                        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
                      }}
                      aria-required="true"
                      aria-invalid={errors.consent ? 'true' : undefined}
                      aria-describedby={errors.consent ? 'contact-consent-error' : undefined}
                    />
                    {' '}I accept the{' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer">privacy policy</Link>
                    {' '}and agree that the school may use these details to reply.<span aria-hidden="true"> *</span>
                  </label>
                  {errors.consent && <span id="contact-consent-error" className="error-message">{errors.consent}</span>}
                </div>

                <Turnstile onToken={setTurnstileToken} resetSignal={turnstileReset} />

                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>

          <div className="map-section">
            <h2 className="section-title">Visit Us</h2>
            <div className="map-placeholder">
              {settings.map_embed_url && (
                <iframe
                  title={`${settings.school_name} Location`}
                  src={settings.map_embed_url}
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              )}
              {settings.map_url && (
                <p style={{ marginTop: '12px' }}>
                  <a
                    href={settings.map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                  >
                    Open in Google Maps
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
